import CustomReplacementCostInput from "./CustomReplacementCostInput";
import EstatedAddress from "./EstatedAddress";
import EstatedStructures from "./EstatedStructures";
import Scraper from "./Scraper";
import ReplacementCosts from "./ReplacementCosts";
import EstatedParcels from "./EstatedParcels";
import EstatedOwner from "./EstatedOwner";
import EstatedTax from "./EstatedTax";
import EstatedAssessment from "./EstatedAssessment";
import EstatedDeed from "./EstatedDeed";
import EstatedMarketAssessment from "./EstatedMarketAssessment";
import EstatedMetadata from "./EstatedMetadata";
import EstatedMortgage from "./EstatedMortgage";
import EstatedOtherArea from "./EstatedOtherArea";
import EstatedOtherFeature from "./EstatedOtherFeature";
import EstatedOtherImprovement from "./EstatedOtherImprovement";
import EstatedValuations from "./EstatedValuations";
import ReplacementCostsByZipCode from "./ReplacementCostsByZipCode";
import PropertyInfo from "./PropertyInfo";
import FireStation from "./FireStation";
import CensusDataByZipCode from "./CensusDataByZipCode";
import CensusDataByState from "./CensusDataByState";
import CensusDataByNation from "./CensusDataByNation";

export default class EstatedProperty {
  constructor(values) {
    this._estimated_rent = values.estimated_rent;
    this.estimated_premium = values.estimated_premium;
    this.custom_replacement_cost_inputs = (
      values.custom_replacement_cost_inputs || []
    ).map((_) => new CustomReplacementCostInput(_));
    this.address = new EstatedAddress(values);
    this.assessments = (values.assessments || []).map(
      (_) => new EstatedAssessment(_)
    );
    this.deeds = (values.deeds || []).map((_) => new EstatedDeed(_));
    this.market_assessments = (values.market_assessments || []).map(
      (_) => new EstatedMarketAssessment(_)
    );
    this.metadata = new EstatedMetadata(values);
    this.mortgages = (values.mortgages || []).map(
      (_) => new EstatedMortgage(_)
    );
    this.other_areas = (values.other_areas || []).map(
      (_) => new EstatedOtherArea(_)
    );
    this.other_features = (values.other_features || []).map(
      (_) => new EstatedOtherFeature(_)
    );
    this.other_improvements = (values.other_improvements || []).map(
      (_) => new EstatedOtherImprovement(_)
    );
    this.owners = (values.owners || []).map((_) => new EstatedOwner(_));
    this.parcels = new EstatedParcels(values);
    this.structures = new EstatedStructures(values);
    this.taxes = (values.taxes || []).map((_) => new EstatedTax(_));
    this.valuations = new EstatedValuations(values);
    this.scraper = new Scraper(values);
    this.replacement_costs = new ReplacementCosts(values);
    this.replacement_cost_by_zip_codes = new ReplacementCostsByZipCode(values);
    this.property_info = new PropertyInfo(
      this.address,
      this.structures,
      this.parcels,
      this.taxes[0] || {},
      this.scraper,
      this.estimated_premium || {
        2500: {},
        "2500_water_backup": {},
        5000: {},
        "5000_water_backup": {},
      },
      values.crime_grade
    );
    this.fire_stations = (values.fire_stations || []).map(
      (_) => new FireStation(_)
    );
    this.census_data_by_zip_code = new CensusDataByZipCode(values);
    this.census_data_by_state = new CensusDataByState(values);
    this.census_data_by_nation = new CensusDataByNation(values);
  }

  get secondMortgages() {
    const secondMortgageTypes = [
      "REVOLVING CREDIT",
      "STAND ALONE SECOND",
      "2ND MORTGAGE MADE TO COVER DOWN PAYMENT",
      "NON PURCHASE MONEY MORTGAGE",
      "AGGREGATE AMOUNT",
    ];
    const latestLoanBuyer = this.latestLoanInfo?.buyer_name;
    const filteredMortgages = this.mortgages.filter(
      (_) =>
        secondMortgageTypes.indexOf(_.loan_type) > -1 &&
        _.primaryBuyerName === latestLoanBuyer &&
        _.due_date &&
        new Date(_.due_date.replace(/-/g, "/")) > new Date()
    );
    return filteredMortgages;
  }

  get latestLoanInfo() {
    const latestDeed = this.deeds.find((_) => _.loan_due_date);
    const latestDeedDate =
      latestDeed && latestDeed.original_contract_date
        ? new Date(latestDeed.original_contract_date.replace(/-/g, "/"))
        : undefined;
    const newMortgageTypes = [
      "NEW CONVENTIONAL",
      "FEDERAL HOUSING ADMINISTRATION",
      "FANNIE MAE OR FREDDIE MAC",
      "ADJUSTABLE RATE MORTGAGE",
      "BALLOON",
      "STATE VETERANS",
      "ASSUMPTION",
      "VETERAN AFFAIRS",
    ];
    const filteredMortgages = this.mortgages.filter(
      (_) => newMortgageTypes.indexOf(_.loan_type) > -1
    );
    const latestMortgage = filteredMortgages.length
      ? filteredMortgages[0]
      : undefined;
    const latestMortgageDate =
      latestMortgage && latestMortgage.original_contract_date
        ? new Date(latestMortgage.original_contract_date.replace(/-/g, "/"))
        : undefined;
    const serializeLatestDeed = () => {
      return {
        loan_amount: latestDeed.loan_amount,
        original_contract_date: latestDeedDate,
        due_date: latestDeed.loan_due_date
          ? new Date(latestDeed.loan_due_date.replace(/-/g, "/"))
          : null,
        lender_legal_name: latestDeed.lender_name,
        lender_type: latestDeed.lender_type,
        loan_type: latestDeed.loan_type,
        sale_price:
          latestDeed.sale_price || (this.scraper || {}).last_sold_price,
        buyer_name: latestDeed.primaryBuyerName,
      };
    };
    const serializeLatestMortgage = () => {
      return {
        loan_amount: latestMortgage.loan_amount,
        original_contract_date: latestMortgageDate,
        due_date: latestMortgage.due_date
          ? new Date(latestMortgage.due_date.replace(/-/g, "/"))
          : null,
        lender_legal_name: latestMortgage.lender_legal_name,
        lender_type: latestMortgage.lender_type,
        loan_type: latestMortgage.loan_type,
        sale_price: (this.scraper || {}).last_sold_price,
        buyer_name: latestMortgage.primaryBuyerName,
      };
    };
    const monthDiff = (dateFrom, dateTo) => {
      return (
        dateTo.getMonth() -
        dateFrom.getMonth() +
        12 * (dateTo.getFullYear() - dateFrom.getFullYear())
      );
    };
    if (!latestDeedDate && !latestMortgageDate) {
      return null;
    } else if (!latestDeedDate) {
      return serializeLatestMortgage();
    } else if (!latestMortgageDate) {
      return serializeLatestDeed();
    } else {
      const diff = monthDiff(latestDeedDate, latestMortgageDate);
      return diff < 0 ? serializeLatestDeed() : serializeLatestMortgage();
    }
  }

  get yearBuilt() {
    return this.structures.year_built || this.scraper.year_built;
  }

  get sqft() {
    return this.structures.total_area_sq_ft || this.scraper.sqft;
  }

  get estimatedReplacementCost() {
    return Math.round(this.replacement_costs.avg_std);
  }

  get estimatedRent() {
    let estimatedRent =
      (this._estimated_rent || {}).scraped ||
      (this._estimated_rent || {}).computed;
    if (!estimatedRent && (this.scraper.estimated_rent_per_sqft || []).length) {
      const estatedBedCount = this.structures.beds_count;
      let estimatedRentPerSqft =
        this.scraper.estimated_rent_per_sqft[0].value[estatedBedCount];
      if (!estimatedRentPerSqft) {
        estimatedRentPerSqft =
          this.scraper.estimated_rent_per_sqft[0].value.all;
      }
      if (estimatedRentPerSqft && this.sqft) {
        estimatedRent = Math.round(estimatedRentPerSqft.median * this.sqft);
      }
    }
    return estimatedRent;
  }

  get estimatedValue() {
    let estimatedPropertyValue = this.scraper.estimated_value;
    if (
      !estimatedPropertyValue &&
      (this.estimated_value_per_sqft || []).length
    ) {
      const estatedBedCount = this.structures.beds_count;
      let estimatedValuePerSqft =
        this.scraper.estimated_value_per_sqft[0].value[estatedBedCount];
      if (!estimatedValuePerSqft) {
        estimatedValuePerSqft =
          this.scraper.estimated_value_per_sqft[0].value.all;
      }
      if (estimatedValuePerSqft && this.sqft) {
        estimatedPropertyValue = Math.round(
          estimatedValuePerSqft.median * this.sqft
        );
      }
    }
    return estimatedPropertyValue;
  }

  get estimatedTiv() {
    return 12 * this.estimatedRent + this.estimatedReplacementCost;
  }

  get balconySqft() {
    const areas = this.other_areas.filter(
      (_) => _.area_type === "BALCONY OR OVERHANG"
    );
    return areas
      .map((_) => parseInt(_.area_sq_ft, 10))
      .reduce((a, b) => a + b, 0);
  }

  get woodDeckSqft() {
    const areas = this.other_improvements.filter(
      (_) => _.impr_type === "WOOD DECK" || _.impr_type === "SYNTHETIC DECK"
    );
    return areas
      .map((_) => {
        const arr = _.impr_sq_ft.split("X");
        if (arr.length === 1) {
          return parseInt(arr[0], 10);
        } else {
          return parseInt(arr[0], 10) * parseInt(arr[1], 10);
        }
      })
      .reduce((a, b) => a + b, 0);
  }

  get coveredPorchSqft() {
    const areas = this.other_areas.filter(
      (_) =>
        [
          "COVERED PORCH",
          "ENCLOSED PORCH",
          "SCREENED PORCH",
          "GLASS ENCLOSED PORCH",
          "CHATTAHOOCHEE PORCH",
        ].indexOf(_.area_type) > -1
    );
    return areas
      .map((_) => parseInt(_.area_sq_ft, 10))
      .reduce((a, b) => a + b, 0);
  }

  appendCustomReplacementCostInput(val) {
    this.custom_replacement_cost_inputs = [
      ...this.custom_replacement_cost_inputs,
      new CustomReplacementCostInput(val),
    ];
  }
}
